import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Quiz from '../Quiz';
import { auth, db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import './LessonModules.css';
import { checkLessonAccess } from '../../utils/checkLessonAccess'; 
import { saveLastLocation } from '../../utils/lastLocation';

const LessonReading = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [checkingAccess, setCheckingAccess] = useState(true);
  const [warning, setWarning] = useState('');
  const [loading, setLoading] = useState(true);

  const [lessonTitle, setLessonTitle] = useState('');
  const [backgroundImage, setBackgroundImage] = useState('');
  const [readingPassage, setReadingPassage] = useState(null);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [savedProgress, setSavedProgress] = useState(null);

  const [videoUrl, setVideoUrl] = useState('');
  const [videoPoster, setVideoPoster] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);

  const [currentIdx, setCurrentIdx] = useState(0);
  const [activeInsertionSentence, setActiveInsertionSentence] = useState("");
  const [selectedSquare, setSelectedSquare] = useState(null);

  // 🔹 NEW: Refresh function to prevent data loss in Firebase
  const refreshProgress = async () => {
  const userId = auth.currentUser?.uid;
  if (userId) {
    // Force a fetch from the exact same path Quiz.js writes to
    const progressRef = doc(db, 'users', userId, 'lessons', `lesson${lessonId}`);
    const progressSnap = await getDoc(progressRef);
    if (progressSnap.exists()) {
      const newData = progressSnap.data();
      // Only update if there is actually a questions array
      if (newData.readingPassage?.questions) {
        setSavedProgress(newData);
      }
    }
  }
};

  useEffect(() => {
    saveLastLocation(location.pathname);
  }, [location.pathname]);

 useEffect(() => {
  const fetchReadingData = async () => {
    try {
      const response = await fetch(`/data/lesson${lessonId}-reading.json`);
      const data = await response.json();
      setLessonTitle(data.title);
      setBackgroundImage(data.backgroundImage);
      setReadingPassage(data.readingPassage);
      setQuizQuestions(data.quiz);
      setVideoUrl(data.videoUrl || '');
      setVideoPoster(data.videoPoster || '');

      const insertionQ = data.quiz.find(q => q.type === "insertion");
      if (insertionQ) setActiveInsertionSentence(insertionQ.targetSentence);

      // 🔹 Fetch initial progress
      const userId = auth.currentUser?.uid;
      if (userId) {
        const progressRef = doc(db, 'users', userId, 'lessons', `lesson${lessonId}`);
        const progressSnap = await getDoc(progressRef);
        if (progressSnap.exists()) setSavedProgress(progressSnap.data());
      }
    } catch (error) { console.error(error); } finally { setLoading(false); }
  };
  fetchReadingData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [lessonId]); // Add eslint hint to ignore the refreshProgress warning

  const renderCleanText = (text) => {
    if (!text) return null;
    const cleanText = text.replace(/\[■\d+\]/g, '');
    return <span dangerouslySetInnerHTML={{ __html: cleanText }} />;
  };

  const renderInteractiveText = (text) => {
    if (!text) return null;
    const parts = text.split(/(\[■\d+\])/g);
    return parts.map((part, i) => {
      if (part.match(/\[■\d+\]/)) {
        const isSelected = selectedSquare === part;
        return (
          <span key={i} style={styles.insertionContainer}>
            {isSelected && <span style={styles.insertedPreview}>{activeInsertionSentence}</span>}
            <button
              className={`insertion-square ${isSelected ? 'active' : ''}`}
              onClick={() => setSelectedSquare(part)}
              style={isSelected ? { ...styles.square, ...styles.activeSquare } : styles.square}
            >
              {isSelected ? "●" : "■"}
            </button>
          </span>
        );
      }
      return <span key={i} dangerouslySetInnerHTML={{ __html: part }} />;
    });
  };

  if (loading) return <div>Loading...</div>;

  const standardQs = quizQuestions.filter(q => q.type !== 'insertion');
  const insertionQ = quizQuestions.filter(q => q.type === 'insertion');
// 🔹 Check if all questions are answered
  const checkReadingCompletion = () => {
    if (!quizQuestions || !savedProgress?.readingPassage?.questions) return false;

    const answeredIds = savedProgress.readingPassage.questions.map(q => String(q.questionId));
    
    // Check if every question in the original JSON exists in the answered list
    const allAnswered = quizQuestions.every(q => answeredIds.includes(String(q.id)));
    
    return allAnswered;
  };

  const isReadingFinished = checkReadingCompletion();
 return (
  <div className="lesson-container">
      <div
        className="hero-section"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '60px 20px',
          color: '#fff',
          textAlign: 'center'
        }}
      >
        <h1>{lessonTitle}</h1>
      </div>

   {/* 2. VIDEO SECTION */}
      {videoUrl && (
        <div style={styles.videoSection}>
          {!isPlaying ? (
            <div style={styles.videoPosterBox} onClick={() => setIsPlaying(true)}>
              <img 
                // 🔹 FIX: If videoPoster is missing in JSON, use the default file
                src={videoPoster || '/images/default-video-cover.jpg'} 
                alt="Lecture Thumbnail" 
                style={styles.videoImg} 
                // Optional: Handle case where the file itself is missing from the folder
                onError={(e) => { e.target.src = '/images/default-video-cover.jpg'; }}
              />
              <div style={styles.playBtn}> ▶</div>
            </div>
          ) : (
            <video 
              src={videoUrl} 
              controls 
              autoPlay 
              style={styles.videoTag} 
              controlsList="nodownload" 
            />
          )}
        </div>
      )}

      <div className="reading-split-view" style={styles.splitGrid}>
        <div className="text-column" style={styles.textColumn}>
          <h2 style={styles.columnTitle}>Academic Passage</h2>
          <div style={styles.scrollableContent}>
            {readingPassage?.paragraphs?.map((para, index) => (
              <p key={index} style={styles.paragraphText}>{renderCleanText(para)}</p>
            ))}
          </div>
        </div>

        <div className="quiz-column" style={styles.quizColumn}>
          <div style={styles.quizCard}>
            <div style={styles.quizHeader}>
              <span style={styles.qCount}>Question {currentIdx + 1} of {standardQs.length}</span>
            </div>
            
            <div style={styles.quizBody}>
               <Quiz 
                  key={`q-${standardQs[currentIdx]?.id}`} 
                  data={[standardQs[currentIdx]]} 
                  lessonId={lessonId}
                  savedProgress={savedProgress?.readingPassage?.questions}
                  onSaveSuccess={refreshProgress} // 🔹 Pass refresh trigger here
                />
            </div>

            <div style={styles.carouselNav}>
              <button 
                disabled={currentIdx === 0} 
                onClick={() => { setCurrentIdx(prev => prev - 1); refreshProgress(); }}
                style={{ ...styles.navBtn, opacity: currentIdx === 0 ? 0.4 : 1 }}
              > PREVIOUS </button>
              <button 
                disabled={currentIdx === standardQs.length - 1} 
                onClick={() => { setCurrentIdx(prev => prev + 1); refreshProgress(); }}
                style={{ ...styles.navBtn, opacity: currentIdx === standardQs.length - 1 ? 0.4 : 1 }}
              > NEXT </button>
            </div>
          </div>
        </div>
      </div>

      <div style={styles.insertionZone}>
        <h2 style={styles.columnTitle}> Sentence Insertion</h2>
        <div style={styles.targetBox}>
          <strong>Target Sentence:</strong><br/>
          "{activeInsertionSentence}"
        </div>
        {readingPassage?.paragraphs?.map((para, index) => (
          <p key={index} style={styles.paragraphText}>{renderInteractiveText(para)}</p>
        ))}
        <Quiz 
          key="insertion-quiz-block"
          data={insertionQ} 
          lessonId={lessonId} 
          savedProgress={savedProgress?.readingPassage?.questions}
          externalSelectedSquare={selectedSquare}
          onSaveSuccess={refreshProgress} // 🔹 Pass refresh trigger here
        />
      </div>
{/* 5. FOOTER */}
      <div style={{ textAlign: 'center', padding: '60px 0', backgroundColor: '#fff', borderTop: '1px solid #eee' }}>
        
        {!isReadingFinished && (
           <p style={{ color: '#dc2626', fontWeight: 'bold', marginBottom: '15px' }}>
             ⚠️ Please answer all questions to unlock the Writing Module.
           </p>
        )}

        <button 
          style={{
            ...styles.finishBtn,
            opacity: isReadingFinished ? 1 : 0.5,
            cursor: isReadingFinished ? 'pointer' : 'not-allowed',
            backgroundColor: isReadingFinished ? '#10b981' : '#64748b' // Turns green when ready!
          }} 
          onClick={() => isReadingFinished && navigate(`/lesson/${lessonId}/writing`)}
          disabled={!isReadingFinished}
        >
          {isReadingFinished ? "PROCEED TO LISTEN AND WRITE →" : "FINISH READING MODULE (LOCKED)"}
        </button>
      </div>
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <button style={styles.finishBtn} onClick={() => navigate(`/dashboard`)}>
          TAKE A BREAK AND & GO TO DASHBOARD
        </button>
        
      </div>
    </div>
  );
};

export default LessonReading;

const styles = {
  mainWrapper: { 
    backgroundColor: '#f0f2f5', 
    minHeight: '100vh', 
    paddingBottom: '60px' 
  },
 hero: { 
    height: '400px', 
    backgroundSize: 'cover', 
    backgroundPosition: 'center', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    position: 'relative',
    color: '#fff', 
    textAlign: 'center',
    marginBottom: '50px',
  },
  heroTitle: {
    fontSize: '3.5rem', 
    fontWeight: '800',
    letterSpacing: '-1px',
    margin: 0,
    padding: '0 20px',
    fontFamily: "'Merriweather', serif",
    textShadow: '2px 4px 10px rgba(0,0,0,0.3)', 
    maxWidth: '900px',
    lineHeight: '1.2'
  },
  
  videoSection: { 
    maxWidth: '850px', 
    margin: '-60px auto 40px', // Pulls the video up into the hero slightly for a modern look
    padding: '0 20px',
    position: 'relative',
    zIndex: 10
  },
  videoPosterBox: { 
    position: 'relative', 
    cursor: 'pointer', 
    borderRadius: '15px', 
    overflow: 'hidden', 
    boxShadow: '0 10px 30px rgba(0,0,0,0.15)' 
  },
  videoImg: { width: '100%', display: 'block' },
  playBtn: { 
    position: 'absolute', 
    top: '50%', 
    left: '50%', 
    transform: 'translate(-50%, -50%)', 
    backgroundColor: '#1e3a8a', 
    color: '#fff', 
    padding: '12px 24px', 
    borderRadius: '30px', 
    fontWeight: 'bold',
    boxShadow: '0 4px 10px rgba(0,0,0,0.3)'
  },
  videoTag: { 
    width: '100%', 
    borderRadius: '15px', 
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)' 
  },

  // 🔹 THE FIX: Contain the width and center the grid
  splitGrid: { 
    display: 'grid', 
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', // Stacks perfectly on mobile
    gap: '30px', 
    maxWidth: '1200px', // Prevents stretching on ultra-wide screens
    margin: '0 auto', 
    padding: '0 20px',
    alignItems: 'start'
  },
  
  textColumn: { 
    backgroundColor: '#fff', 
    padding: '30px', 
    borderRadius: '15px', 
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    maxHeight: '750px', // Keep it in view
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid #e5e7eb'
  },
  scrollableContent: { 
    overflowY: 'auto', 
    flex: 1, 
    paddingRight: '15px' 
  },
  
  quizColumn: { 
    position: 'sticky', 
    top: '20px',
    maxWidth: '500px', // Prevents the quiz from becoming too wide
    margin: '0 auto'   // Centers the quiz card if it's smaller than the column
  },
  quizCard: { 
    backgroundColor: '#fff', 
    padding: '30px', 
    borderRadius: '15px', 
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    minHeight: '450px',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid #e5e7eb'
  },

  columnTitle: { 
    color: '#1e3a8a', 
    fontSize: '1.4rem',
    borderBottom: '2px solid #f3f4f6', 
    paddingBottom: '12px', 
    marginBottom: '20px',
    fontFamily: 'Merriweather, serif'
  },
  paragraphText: { 
    lineHeight: '1.8', 
    marginBottom: '20px', 
    fontSize: '1.05rem', 
    color: '#374151',
    fontFamily: 'Georgia, serif'
  },
  
  quizHeader: { 
    marginBottom: '20px', 
    borderBottom: '1px solid #f3f4f6', 
    paddingBottom: '10px' 
  },
  qCount: { fontWeight: 'bold', color: '#6b7280', fontSize: '0.9rem' },
  quizBody: { flex: 1 },
  
  carouselNav: { 
    display: 'flex', 
    gap: '12px', 
    marginTop: '25px',
    paddingTop: '15px',
    borderTop: '1px solid #f3f4f6'
  },
  navBtn: { 
    flex: 1, 
    padding: '12px', 
    cursor: 'pointer', 
    backgroundColor: '#fff', 
    border: '1px solid #1e3a8a', 
    color: '#1e3a8a', 
    borderRadius: '8px', 
    fontWeight: 'bold',
    transition: 'all 0.2s'
  },

  insertionZone: { 
    maxWidth: '900px', 
    margin: '60px auto', 
    backgroundColor: '#fff', 
    padding: '40px', 
    borderRadius: '15px', 
    border: '1px solid #e5e7eb',
    boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
  },
  targetBox: { 
    backgroundColor: '#fff9db', 
    padding: '20px', 
    borderRadius: '8px', 
    borderLeft: '5px solid #f59e0b', 
    marginBottom: '30px',
    fontSize: '1.1rem',
    fontStyle: 'italic'
  },
  
  insertionContainer: { display: 'inline-flex', alignItems: 'center' },
  square: { 
    width: '26px', 
    height: '26px', 
    backgroundColor: '#1e3a8a', 
    color: '#fff', 
    border: 'none', 
    borderRadius: '4px', 
    margin: '0 6px', 
    cursor: 'pointer' 
  },
  activeSquare: { backgroundColor: '#f59e0b' },
  insertedPreview: { 
    backgroundColor: '#fff3cd', 
    fontStyle: 'italic', 
    padding: '2px 8px', 
    marginRight: '6px',
    borderRadius: '4px',
    color: '#856404'
  },

  finishBtn: { 
    backgroundColor: '#1e3a8a', 
    color: '#fff', 
    padding: '16px 50px', 
    borderRadius: '50px', 
    border: 'none', 
    fontWeight: 'bold', 
    fontSize: '1.1rem', 
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(30, 58, 138, 0.3)'
  }
};